import { useCallback, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogClose, Button, Icon } from '@ideoz/ui';
import { Loader2, FileSearch, X, Check, AlertCircle } from 'lucide-react';
import { iceService } from '@ideoz/core';
import { useScopeSuggestions, type AnalyzeStage } from './useScopeSuggestions';
import { ScopeSuggestionCard } from './ScopeSuggestionCard';

interface UploadScopeFilesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  startupId: string;
  /** Called after a suggested scope is created, so the list can refresh. */
  onScopeAdded?: () => void;
  /**
   * Queues an ICE evaluation for a newly-added suggested scope. Prefer the
   * page's ICE facade `evaluate` (drives the "Scoring…" badge); falls back to a
   * raw fire-and-forget API call when omitted.
   */
  onScopeEvaluate?: (scopeId: string) => void;
}

const fileBaseName = (name: string): string =>
  name.lastIndexOf('.') > 0 ? name.substring(0, name.lastIndexOf('.')) : name;

const fileExt = (name: string): string =>
  name.lastIndexOf('.') > 0 ? name.substring(name.lastIndexOf('.') + 1).toUpperCase() : '';

/** Ordered pipeline stages, shown as a checklist while analysis runs. */
const STAGES: { key: AnalyzeStage; label: string; activeLabel: string; doneLabel: string }[] = [
  {
    key: 'upload',
    label: 'Upload files',
    activeLabel: 'Uploading files…',
    doneLabel: 'Files uploaded',
  },
  {
    key: 'indexing',
    label: 'Process files',
    activeLabel: 'Processing files…',
    doneLabel: 'Files processed',
  },
  {
    key: 'analyzing',
    label: 'Find scopes',
    activeLabel: 'Finding scopes…',
    doneLabel: 'Scopes found',
  },
];

interface StagePipelineProps {
  stage: AnalyzeStage | null;
  /** True once every stage has completed successfully. */
  done: boolean;
  /** True if the flow stopped on an error at the current stage. */
  errored: boolean;
  /** Files uploaded so far / total — annotates the upload stage. */
  uploaded: number;
  total: number;
  /** Latest streamed status label — shown under the active analyzing stage. */
  detail?: string;
}

/**
 * Three-stage status indicator. Replaces the old percentage bar, which could
 * only ever read 0/50/100 because just one of the three phases has countable
 * units — the other two are indeterminate waits. A checklist tells the user
 * what is happening during those waits without promising a fake percentage.
 */
function StagePipeline({
  stage,
  done,
  errored,
  uploaded,
  total,
  detail,
}: StagePipelineProps): JSX.Element {
  const currentIndex = done ? STAGES.length : stage ? STAGES.findIndex((s) => s.key === stage) : 0;

  return (
    <ol className="mt-5 flex flex-col gap-1" data-testid="scope-stage-pipeline">
      {STAGES.map((s, i) => {
        const isDone = i < currentIndex;
        const isActive = i === currentIndex && !done;
        const isError = errored && isActive;
        const isUpload = s.key === 'upload';

        return (
          <li key={s.key} className="flex items-start gap-2.5 px-2.5 py-1.5">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              {isError ? (
                <AlertCircle className="h-4 w-4 text-red-500" />
              ) : isDone ? (
                <Check className="h-4 w-4 text-primary-600" strokeWidth={2.5} />
              ) : isActive ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-500" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-600" />
              )}
            </span>
            <span className="flex min-w-0 flex-col">
              <span
                className={
                  isActive
                    ? 'text-sm font-medium text-gray-800 dark:text-gray-100'
                    : isDone
                      ? 'text-sm text-gray-500 dark:text-gray-400'
                      : 'text-sm text-gray-400 dark:text-gray-500'
                }
              >
                {isError
                  ? `${s.label} failed`
                  : isActive
                    ? s.activeLabel
                    : isDone
                      ? s.doneLabel
                      : s.label}
                {isUpload && total > 1 && (isActive || isDone) && (
                  <span className="ml-1 text-xs text-gray-400">
                    ({isDone ? total : uploaded}/{total})
                  </span>
                )}
              </span>
              {isActive && s.key === 'analyzing' && detail && (
                <span className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                  {detail}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function UploadScopeFilesDialog({
  isOpen,
  onClose,
  startupId,
  onScopeAdded,
  onScopeEvaluate,
}: UploadScopeFilesDialogProps): JSX.Element {
  const { phase, stage, uploadedCount, steps, suggestions, error, analyze, ignore, add, reset } =
    useScopeSuggestions(startupId);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const isAnalyzing = phase === 'analyzing';
  const latestStepLabel = steps.length > 0 ? steps[steps.length - 1].label : undefined;
  const noResults = phase === 'done' && suggestions.length === 0 && !error;
  const hasFiles = selectedFiles.length > 0;

  const handleClose = useCallback(() => {
    reset();
    setSelectedFiles([]);
    onClose();
  }, [reset, onClose]);

  const onFiles = useCallback(
    (files: FileList | File[] | null) => {
      const list = files ? Array.from(files) : [];
      if (list.length) {
        setSelectedFiles(list);
        void analyze(list);
      }
      if (inputRef.current) inputRef.current.value = '';
    },
    [analyze]
  );

  const handleAdd = useCallback(
    async (id: string) => {
      const scopeId = await add(id);
      onScopeAdded?.();
      // Queue an evaluation for the new scope so its card shows "Scoring…".
      if (scopeId) {
        if (onScopeEvaluate) onScopeEvaluate(scopeId);
        else
          void iceService.evaluateScope(startupId, scopeId).catch((err) => {
            console.error('Failed to trigger scope evaluation', err);
          });
      }
    },
    [add, onScopeAdded, onScopeEvaluate, startupId]
  );

  const hasSuggestions = suggestions.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-2xl"
        // Don't close on backdrop / outside click — an analysis may be running.
        // Use the explicit close button instead.
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Add multiple scopes
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Attach your files and we will create the scopes for you.
            </p>
          </div>
          <DialogClose
            className="rounded-sm text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </DialogClose>
        </header>

        {/* Upload area — same UX as the Files upload dialog (list + button).
            Hidden once suggestions arrive to give the scopes list more room. */}
        {!hasSuggestions && (
          <div className="flex flex-col rounded-lg bg-background p-3">
            <div className="max-h-[45vh] overflow-y-auto">
              {!hasFiles ? (
                <div className="flex flex-col items-center justify-center rounded-lg px-4 py-8">
                  <div className="mb-3">
                    <Icon icon={FileSearch} size={34} strokeWidth={1.5} color="#252B37" />
                  </div>
                  <span className="mb-1 text-base font-medium text-gray-700">
                    Upload your files
                  </span>
                  <span className="text-center text-sm text-gray-500">
                    To create scopes, please upload your files.
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedFiles.map((file, i) => (
                    <div
                      key={`${file.name}-${i}`}
                      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3"
                    >
                      <div className="relative flex-shrink-0">
                        {stage === 'upload' ? (
                          <div className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-gray-200 border-t-primary-600" />
                        ) : isAnalyzing ? (
                          <Check className="h-[18px] w-[18px] text-primary-600" strokeWidth={2.5} />
                        ) : (
                          <Icon icon={FileSearch} size={18} strokeWidth={2} color="#029B8C" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col">
                          <div className="truncate text-base font-medium text-gray-900">
                            {fileBaseName(file.name)}
                          </div>
                          <div className="text-sm text-gray-500">{fileExt(file.name)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
            />

            {/* Upload button — fixed at the bottom, like the Files dialog. */}
            <div className="mt-4 flex justify-end border-t border-gray-200 pt-4">
              <Button
                onClick={() => inputRef.current?.click()}
                disabled={isAnalyzing}
                variant="secondary-gray"
                size="sm"
              >
                {hasFiles ? 'Upload new files' : 'Upload files'}
              </Button>
            </div>
          </div>
        )}

        {/* Staged pipeline — replaces the old percentage bar. Shown while
            analysing and briefly once done; hidden once suggestions render. */}
        {(isAnalyzing ||
          phase === 'error' ||
          (phase === 'done' && !hasSuggestions && !noResults)) && (
          <StagePipeline
            stage={stage}
            done={phase === 'done'}
            errored={phase === 'error'}
            uploaded={uploadedCount}
            total={selectedFiles.length}
            detail={latestStepLabel}
          />
        )}

        {/* No scopes detected */}
        {noResults && (
          <div className="mt-6 flex flex-col items-center gap-2 rounded-lg bg-gray-50 px-4 py-8 text-center dark:bg-gray-800/50">
            <FileSearch size={28} className="text-gray-400" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              No scopes detected
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              We couldn&apos;t find anything scope-worthy in these files. Try uploading more
              detailed material.
            </p>
          </div>
        )}

        {error && <p className="mt-4 text-sm text-red-500">{error}</p>}

        {/* Suggestions — scrollable so a long list doesn't blow out the dialog. */}
        {hasSuggestions && (
          <div className="mt-4 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {suggestions.map((s, i) => (
              <ScopeSuggestionCard
                key={s.id}
                suggestion={s}
                index={i}
                onAdd={handleAdd}
                onIgnore={ignore}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
